import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { LifeBuoy, X, Send, Plus, MessageSquare } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const STATUS_LABEL = {
  aberto: "Aberto", em_atendimento: "Em atendimento", aguardando_usuario: "Aguardando você",
  aguardando_equipe: "Aguardando equipe", resolvido: "Resolvido", fechado: "Fechado",
};

export default function FloatingSupport() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const ticketBase = location.pathname.startsWith("/revenda") ? "/revenda"
    : location.pathname.startsWith("/admin") ? "/admin"
    : location.pathname.startsWith("/suporte") ? "/suporte"
    : "/painel";
  const [open, setOpen] = useState(false);
  const [view, setView] = useState("list");
  const [tickets, setTickets] = useState([]);
  const [subject, setSubject] = useState("");
  const [category, setCategory] = useState("Ativação");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);

  const logged = user && user !== false;

  const loadTickets = async () => {
    if (!logged) return;
    try {
      const { data } = await api.get("/support/tickets");
      setTickets(Array.isArray(data) ? data : (data?.items || []));
    } catch {}
  };

  useEffect(() => {
    if (open && logged) loadTickets();
  }, [open, user]);

  const create = async () => {
    if (!subject.trim() || !message.trim()) {
      toast.error("Preencha o assunto e a mensagem.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/support/tickets", { subject, category, message });
      toast.success("Ticket criado! Nossa equipe irá responder.");
      setSubject("");
      setMessage("");
      setView("list");
      loadTickets();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <button
        onClick={() => setOpen((o) => !o)}
        className="fab-offset fixed bottom-6 right-4 z-50 flex h-12 w-12 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-lg transition-transform hover:scale-105 sm:right-6 sm:h-14 sm:w-14"
        data-testid="floating-support-btn"
        aria-label="Suporte"
      >
        {open ? <X className="h-6 w-6" /> : <LifeBuoy className="h-6 w-6 animate-pulse" />}
      </button>

      {open && (
        <div className="panel-offset fixed bottom-24 right-4 z-50 flex max-h-[70vh] w-[92vw] max-w-sm flex-col overflow-hidden rounded-xl border border-border bg-card shadow-2xl sm:right-6" data-testid="support-panel">
          <div className="flex items-center justify-between border-b border-border bg-primary px-4 py-3 text-primary-foreground">
            <span className="font-heading font-semibold">Suporte VULPIX</span>
            <MessageSquare className="h-5 w-5" />
          </div>

          {!logged ? (
            <div className="flex flex-col items-center gap-3 p-6 text-center">
              <p className="text-sm text-muted-foreground">Entre na sua conta para abrir e acompanhar tickets de suporte.</p>
              <Button onClick={() => { setOpen(false); navigate("/login"); }} data-testid="support-login-btn">Entrar</Button>
            </div>
          ) : view === "list" ? (
            <div className="flex flex-1 flex-col overflow-hidden">
              <div className="flex items-center justify-between p-3">
                <span className="text-sm font-medium">Seus tickets</span>
                <Button size="sm" onClick={() => setView("new")} className="gap-1" data-testid="support-new-ticket-btn">
                  <Plus className="h-4 w-4" /> Novo
                </Button>
              </div>
              <div className="flex-1 space-y-2 overflow-y-auto px-3 pb-3">
                {tickets.length === 0 && <p className="py-8 text-center text-sm text-muted-foreground">Nenhum ticket ainda.</p>}
                {tickets.map((t) => (
                  <button
                    key={t.id}
                    onClick={() => { setOpen(false); navigate(`${ticketBase}/tickets/${t.id}`); }}
                    className="w-full rounded-md border border-border p-3 text-left transition-colors hover:bg-accent"
                    data-testid={`support-ticket-${t.id}`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="truncate text-sm font-medium">{t.subject}</span>
                      <Badge variant="secondary" className="shrink-0 text-[10px]">{STATUS_LABEL[t.status]}</Badge>
                    </div>
                    <p className="mt-1 text-xs text-muted-foreground">{t.category}</p>
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <div className="flex flex-col gap-3 p-4">
              <div className="space-y-1.5">
                <Label>Assunto</Label>
                <Input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Resumo do problema" data-testid="support-subject-input" />
              </div>
              <div className="space-y-1.5">
                <Label>Categoria</Label>
                <Select value={category} onValueChange={setCategory}>
                  <SelectTrigger data-testid="support-category-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Ativação">Ativação</SelectItem>
                    <SelectItem value="Créditos">Créditos</SelectItem>
                    <SelectItem value="Conta">Conta</SelectItem>
                    <SelectItem value="Financeiro">Financeiro</SelectItem>
                    <SelectItem value="Geral">Geral</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Mensagem</Label>
                <Textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={4} placeholder="Descreva o que aconteceu..." data-testid="support-message-input" />
              </div>
              <div className="flex gap-2">
                <Button variant="outline" className="flex-1" onClick={() => setView("list")} data-testid="support-cancel-btn">Cancelar</Button>
                <Button className="flex-1 gap-1" onClick={create} disabled={loading} data-testid="support-submit-btn">
                  <Send className="h-4 w-4" /> Enviar
                </Button>
              </div>
            </div>
          )}
        </div>
      )}
    </>
  );
}
